import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Upload, Image, Film, FileImage, Trash2,
  LogOut, Eye, Check, AlertCircle, Plus, X, BarChart3,
  MousePointerClick, TrendingUp, Settings, User, Loader2, Save
} from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ====================== SUB-COMPONENTS ====================== */

const AssetCard = ({ asset, type, onDelete }) => {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <div className="bg-zinc-800/50 border border-white/5 p-4 group" data-testid={`asset-card-${asset.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-white text-sm truncate">{asset.name}</h4>
          <p className="text-xs text-zinc-500 mt-1">Uploaded {new Date(asset.uploaded_at).toLocaleDateString()}</p>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => setShowPreview(true)} className="p-1.5 text-zinc-500 hover:text-white" title="Preview"><Eye className="w-4 h-4" /></button>
          <button onClick={() => onDelete(asset.id)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {type !== "videos" && asset.file_data && (
        <div className="aspect-video bg-zinc-900 rounded overflow-hidden">
          <img src={`data:image/${asset.file_extension};base64,${asset.file_data}`} alt={asset.name} className="w-full h-full object-contain" />
        </div>
      )}
      {type === "videos" && (
        <div className="aspect-video bg-zinc-900 rounded flex items-center justify-center"><Film className="w-8 h-8 text-zinc-600" /></div>
      )}
      {showPreview && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowPreview(false)} className="absolute -top-10 right-0 text-white hover:text-zinc-300"><X className="w-6 h-6" /></button>
            {type !== "videos" && asset.file_data && (
              <img src={`data:image/${asset.file_extension};base64,${asset.file_data}`} alt={asset.name} className="max-w-full max-h-[80vh] object-contain" />
            )}
            {type === "videos" && (
              <div className="bg-zinc-900 p-8 text-center"><Film className="w-16 h-16 text-zinc-600 mx-auto mb-4" /><p className="text-zinc-400">Video preview not available</p></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UploadSection = ({ type, label, icon: Icon, onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const acceptTypes = { logos: "image/png,image/jpeg,image/svg+xml", banners: "image/png,image/jpeg,image/webp", videos: "video/mp4,video/webm,video/quicktime" };

  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      const extension = file.name.split('.').pop().toLowerCase();
      onUpload(type, file.name, base64, extension);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive ? 'border-white/50 bg-white/5' : 'border-white/10 hover:border-white/20'}`}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]); }}
    >
      <input ref={fileInputRef} type="file" accept={acceptTypes[type]} onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
      <Icon className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
      <p className="text-sm text-zinc-400 mb-3">Drag & drop your {label.toLowerCase()} here, or</p>
      <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none">
        <Plus className="w-4 h-4 mr-1" />{uploading ? "Uploading..." : `Browse ${label}`}
      </Button>
    </div>
  );
};

/* ====================== PERFORMANCE TAB ====================== */

const PerformanceTab = ({ getAuthHeader }) => {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPerf = async () => {
      try {
        const res = await axios.get(`${API}/sponsors/me/performance`, { headers: getAuthHeader() });
        setPerf(res.data);
      } catch (err) {
        console.error("Error fetching performance:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPerf();
  }, [getAuthHeader]);

  const fmt = (n) => {
    if (n == null) return '0';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n?.toLocaleString() || '0';
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;

  return (
    <div className="space-y-6" data-testid="performance-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-blue-500/15 to-blue-600/5 border border-blue-500/20 rounded-lg p-4">
          <BarChart3 className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{fmt(perf?.total_ads)}</p>
          <p className="text-xs text-zinc-400">Total Campaigns</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{perf?.active_ads || 0} active</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-gradient-to-br from-orange-500/15 to-orange-600/5 border border-orange-500/20 rounded-lg p-4">
          <Eye className="w-5 h-5 text-orange-400 mb-2" />
          <p className="text-2xl font-bold text-white">{fmt(perf?.total_impressions)}</p>
          <p className="text-xs text-zinc-400">Total Impressions</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-emerald-500/15 to-emerald-600/5 border border-emerald-500/20 rounded-lg p-4">
          <MousePointerClick className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-2xl font-bold text-white">{fmt(perf?.total_clicks)}</p>
          <p className="text-xs text-zinc-400">Total Clicks</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-gradient-to-br from-violet-500/15 to-violet-600/5 border border-violet-500/20 rounded-lg p-4">
          <TrendingUp className="w-5 h-5 text-violet-400 mb-2" />
          <p className="text-2xl font-bold text-white">{perf?.ctr || 0}%</p>
          <p className="text-xs text-zinc-400">Click-Through Rate</p>
        </motion.div>
      </div>

      {/* Placement Breakdown */}
      {perf?.by_placement?.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <h3 className="text-white font-bold text-sm mb-3">Performance by Placement</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 border-b border-white/5">
                  <th className="text-left py-2 pr-4">Placement</th>
                  <th className="text-right py-2 px-3">Ads</th>
                  <th className="text-right py-2 px-3">Impressions</th>
                  <th className="text-right py-2 px-3">Clicks</th>
                  <th className="text-right py-2 pl-3">CTR</th>
                </tr>
              </thead>
              <tbody>
                {perf.by_placement.map((p, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-0">
                    <td className="py-2 pr-4 text-zinc-300 capitalize">{p.placement}</td>
                    <td className="py-2 px-3 text-right text-zinc-400">{p.ads_count}</td>
                    <td className="py-2 px-3 text-right text-zinc-300">{fmt(p.impressions)}</td>
                    <td className="py-2 px-3 text-right text-zinc-300">{fmt(p.clicks)}</td>
                    <td className="py-2 pl-3 text-right text-orange-400 font-medium">{p.ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaigns */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
        <h3 className="text-white font-bold text-sm mb-3">Your Campaigns</h3>
        {(!perf?.campaigns || perf.campaigns.length === 0) ? (
          <div className="text-center py-8">
            <BarChart3 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No active campaigns yet</p>
            <p className="text-zinc-600 text-xs mt-1">Upload assets and contact the admin to set up your ad campaigns</p>
          </div>
        ) : (
          <div className="space-y-3">
            {perf.campaigns.map((c, i) => (
              <div key={c.id || i} className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg border border-white/5">
                {c.image_url ? (
                  <img src={c.image_url} alt="" className="w-16 h-10 object-cover rounded flex-shrink-0" />
                ) : (
                  <div className="w-16 h-10 bg-zinc-700 rounded flex-shrink-0 flex items-center justify-center"><Image className="w-5 h-5 text-zinc-500" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">{c.name}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded ${c.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-xs capitalize">{c.placement} placement</p>
                </div>
                <div className="flex items-center gap-6 text-xs">
                  <div className="text-center">
                    <p className="text-white font-medium">{fmt(c.impressions)}</p>
                    <p className="text-zinc-500">Impr.</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-medium">{fmt(c.clicks)}</p>
                    <p className="text-zinc-500">Clicks</p>
                  </div>
                  <div className="text-center">
                    <p className="text-orange-400 font-medium">{c.ctr}%</p>
                    <p className="text-zinc-500">CTR</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ====================== ACCOUNT SETTINGS TAB ====================== */

const AccountTab = ({ profile, getAuthHeader, onProfileUpdate }) => {
  const [form, setForm] = useState({
    name: profile?.name || '',
    company: profile?.company || '',
    email: profile?.email || '',
    business_type: profile?.business_type || ''
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sponsors/me/profile`, null, {
        params: form,
        headers: getAuthHeader()
      });
      setSuccess('Profile updated successfully');
      onProfileUpdate?.();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="account-tab">
      <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5 space-y-4">
        <h3 className="text-white font-bold text-sm flex items-center gap-2"><User className="w-4 h-4 text-orange-400" /> Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-500 text-xs uppercase mb-1 block">Name</label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm rounded" data-testid="account-name" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase mb-1 block">Company</label>
            <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm rounded" data-testid="account-company" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase mb-1 block">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm rounded" data-testid="account-email" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase mb-1 block">Business Type</label>
            <input type="text" value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm rounded" data-testid="account-business-type" />
          </div>
        </div>
        {success && (
          <div className="flex items-center gap-2 text-green-400 text-xs">
            <Check className="w-3 h-3" /> {success}
          </div>
        )}
        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-sm" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5 space-y-2">
        <h3 className="text-white font-bold text-sm">Account Details</h3>
        <div className="text-xs text-zinc-400 space-y-1">
          <p><span className="text-zinc-500">Username:</span> {profile?.username}</p>
          <p><span className="text-zinc-500">Status:</span> <span className="text-green-400">{profile?.status}</span></p>
          <p><span className="text-zinc-500">Member since:</span> {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

/* ====================== MAIN DASHBOARD ====================== */

export const SponsorDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [assets, setAssets] = useState({ logos: [], banners: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("performance");

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("sponsor_token")}`
  });

  useEffect(() => {
    const token = localStorage.getItem("sponsor_token");
    if (!token) { navigate("/sponsor/login"); return; }
    fetchProfile();
    fetchAssets();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/sponsors/me`, { headers: getAuthHeader() });
      setProfile(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("sponsor_token");
        navigate("/sponsor/login");
      }
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/sponsors/me/assets`, { headers: getAuthHeader() });
      setAssets(response.data);
    } catch (err) {
      console.error("Error fetching assets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (assetType, name, fileData, fileExtension) => {
    setUploading(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(`${API}/sponsors/me/assets`, null, {
        params: { asset_type: assetType, name, file_data: fileData, file_extension: fileExtension },
        headers: getAuthHeader()
      });
      setSuccess("Asset uploaded successfully!");
      fetchAssets();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetType, assetId) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;
    try {
      await axios.delete(`${API}/sponsors/me/assets/${assetType}/${assetId}`, { headers: getAuthHeader() });
      setSuccess("Asset deleted");
      fetchAssets();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sponsor_token");
    localStorage.removeItem("sponsor_id");
    localStorage.removeItem("sponsor_company");
    navigate("/sponsor/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const assetTabs = [
    { id: "logos", label: "Logos", icon: FileImage },
    { id: "banners", label: "Banners", icon: Image },
    { id: "videos", label: "Pre-Roll Videos", icon: Film }
  ];

  const navTabs = [
    { id: "performance", label: "Performance", icon: BarChart3 },
    { id: "assets", label: "Assets", icon: Upload },
    { id: "account", label: "Account", icon: Settings }
  ];

  const currentAssetTab = ["logos", "banners", "videos"].includes(activeTab) ? activeTab : "logos";

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="sponsor-dashboard">
      {/* Header */}
      <header className="bg-black border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img
                  src="https://customer-assets.emergentagent.com/job_363f79c5-085c-4461-8ddb-f89341e71c50/artifacts/832gaaj4_ASN%20Short%20Wordmark.png"
                  alt="Antler Sports Network"
                  className="h-8 w-auto"
                />
              </Link>
              <span className="text-zinc-600">|</span>
              <span className="font-secondary text-sm font-bold uppercase tracking-wider text-white">Sponsor Portal</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-zinc-500 text-xs hidden sm:inline">{profile?.company}</span>
              <button onClick={handleLogout} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Welcome Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900/50 border border-white/10 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-6 h-6 text-white" />
                <h1 className="font-secondary text-2xl font-bold uppercase text-white">{profile?.company || "Welcome"}</h1>
              </div>
              <p className="text-zinc-400 text-sm">Manage your campaigns, assets, and performance metrics.</p>
            </div>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold uppercase">Active Sponsor</span>
          </div>
        </motion.div>

        {/* Alerts */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-500 text-sm">{error}</span>
            <button onClick={() => setError("")} className="ml-auto text-red-500 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 mb-6">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-green-500 text-sm">{success}</span>
            <button onClick={() => setSuccess("")} className="ml-auto text-green-500 hover:text-green-400"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Main Navigation */}
        <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto scrollbar-hide">
          {navTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id || (tab.id === 'assets' && ['logos', 'banners', 'videos'].includes(activeTab))
                  ? 'text-white border-orange-500'
                  : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
              data-testid={`nav-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Performance Tab */}
        {activeTab === "performance" && <PerformanceTab getAuthHeader={getAuthHeader} />}

        {/* Assets Tab */}
        {(activeTab === "assets" || ["logos", "banners", "videos"].includes(activeTab)) && (
          <div>
            {/* Asset Sub-tabs */}
            <div className="flex gap-1 mb-4">
              {assetTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded transition-colors ${
                    currentAssetTab === tab.id ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`asset-tab-${tab.id}`}
                >
                  <tab.icon className="w-3 h-3" />
                  {tab.label}
                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${currentAssetTab === tab.id ? 'bg-white/10 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                    {assets[tab.id]?.length || 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Upload */}
              <div className="lg:col-span-1">
                <h3 className="font-secondary text-sm font-bold uppercase tracking-wider text-white mb-4">
                  Upload New {assetTabs.find(t => t.id === currentAssetTab)?.label}
                </h3>
                <UploadSection
                  type={currentAssetTab}
                  label={assetTabs.find(t => t.id === currentAssetTab)?.label || "File"}
                  icon={assetTabs.find(t => t.id === currentAssetTab)?.icon || Upload}
                  onUpload={handleUpload}
                  uploading={uploading}
                />
                <div className="mt-6 p-4 bg-zinc-900/50 border border-white/5 text-xs text-zinc-500">
                  <p className="font-bold text-zinc-400 mb-2">Asset Guidelines:</p>
                  {currentAssetTab === "logos" && (
                    <ul className="space-y-1 list-disc list-inside"><li>PNG or SVG format recommended</li><li>Transparent background preferred</li><li>Minimum 200x200 pixels</li></ul>
                  )}
                  {currentAssetTab === "banners" && (
                    <ul className="space-y-1 list-disc list-inside"><li>Recommended: 970x250 (Leaderboard)</li><li>Also accepted: 300x250, 728x90</li><li>Max file size: 2MB</li></ul>
                  )}
                  {currentAssetTab === "videos" && (
                    <ul className="space-y-1 list-disc list-inside"><li>MP4 or WebM format</li><li>15-30 seconds recommended</li><li>1920x1080 resolution</li><li>Max file size: 50MB</li></ul>
                  )}
                </div>
              </div>

              {/* Assets Grid */}
              <div className="lg:col-span-2">
                <h3 className="font-secondary text-sm font-bold uppercase tracking-wider text-white mb-4">
                  Your {assetTabs.find(t => t.id === currentAssetTab)?.label}
                </h3>
                {assets[currentAssetTab]?.length === 0 ? (
                  <div className="bg-zinc-900/30 border border-white/5 p-12 text-center">
                    {currentAssetTab === "logos" && <FileImage className="w-12 h-12 text-zinc-700 mx-auto mb-4" />}
                    {currentAssetTab === "banners" && <Image className="w-12 h-12 text-zinc-700 mx-auto mb-4" />}
                    {currentAssetTab === "videos" && <Film className="w-12 h-12 text-zinc-700 mx-auto mb-4" />}
                    <p className="text-zinc-500 mb-2">No {assetTabs.find(t => t.id === currentAssetTab)?.label.toLowerCase()} uploaded yet</p>
                    <p className="text-xs text-zinc-600">Upload your first asset using the form on the left</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {assets[currentAssetTab].map(asset => (
                      <AssetCard key={asset.id} asset={asset} type={currentAssetTab} onDelete={(assetId) => handleDelete(currentAssetTab, assetId)} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === "account" && <AccountTab profile={profile} getAuthHeader={getAuthHeader} onProfileUpdate={fetchProfile} />}
      </main>
    </div>
  );
};

export default SponsorDashboard;
